import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, Button, DatePicker, Card, Row, Col, message, Spin, Typography, Divider, Alert, Select } from 'antd';
import {
    SearchOutlined,
    DownloadOutlined,
    PrinterOutlined,
    HistoryOutlined,
    FilePdfOutlined,
    EditOutlined,
    FileTextOutlined,
    UserOutlined
} from '@ant-design/icons';
import { Tag, Tooltip, Space } from 'antd';
import { orderService } from '../services/orderService';
import dayjs from 'dayjs';
import api from '../services/api';
import html2pdf from 'html2pdf.js';
import vendorSign from '../assets/vendor_sign.png';

const { Title, Text } = Typography;
const { TextArea } = Input;

const VendorAgreementPage = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [orderData, setOrderData] = useState(null);
    const [recentOrders, setRecentOrders] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [messageApi, contextHolder] = message.useMessage();
    const componentRef = useRef();

    // Watch form values for real-time preview updating
    const formValues = Form.useWatch([], form);

    useEffect(() => {
        fetchCompanyDetails();
        fetchRecentOrders();
    }, []);

    const fetchRecentOrders = async () => {
        try {
            const response = await orderService.getAllOrders({ limit: 6 });
            if (response.success) {
                setRecentOrders(response.data || []);
            }
        } catch (error) {
            console.error('Error fetching recent orders:', error);
        }
    };

    const fetchCompanyDetails = async () => {
        try {
            const response = await api.get('/companies');
            const companyList = response.data || [];
            setCompanies(companyList);
            
            if (companyList.length > 0) {
                const defaultComp = companyList[0];
                form.setFieldsValue({
                    selectedCompanyId: defaultComp.id,
                    vendorName: defaultComp.companyName,
                    vendorAddress: defaultComp.address,
                });
            }
        } catch (error) {
            console.error('Error fetching company details:', error);
            messageApi.error('Failed to load company details.');
        }
    };

    const handleSearch = async (orderId) => {
        if (!orderId) {
            messageApi.warning('Please enter an Order ID');
            return;
        }

        setLoading(true);
        try {
            const response = await api.get(`/orders/${orderId}`);

            if (response.success) {
                const order = response.data;
                setOrderData(order);

                const customer = order.customer;
                const fullAddress = [
                    customer.customerAddress,
                    customer.subDistrict,
                    customer.district,
                    customer.pincode ? `Pin Code: ${customer.pincode}` : null
                ].filter(Boolean).join(', ');

                form.setFieldsValue({
                    agreementDate: dayjs(),
                    customerName: customer.customerName,
                    consumerNumber: order.selectedConsumerNumber || customer.consumerNo,
                    customerAddress: fullAddress,
                    projectSize: `${order.plantSizeKw} kW`,
                    orderValue: order.orderValue,
                    termsOfPayment: `● 10% - Advance along with order confirmation / Application on National Portal (www.pmsuryaghar.gov.in)\n● 80% - Signing of this Agreement (In line with Format on National Portal)\n● 5% - Installation of system on the house roof\n● 5% - Installation of Bi-Directional meter and commissioning and approval with MAHADISCOM`
                });
                messageApi.success('Order details loaded successfully');
            }
        } catch (error) {
            console.error('Error fetching order:', error);
            messageApi.error('Order not found or error loading details');
            setOrderData(null);
        } finally {
            setLoading(false);
        }
    };

    const handleGeneratePDF = () => {
        const element = componentRef.current;
        const opt = {
            margin: 10,
            filename: `Vendor_Agreement_${orderData?.id || 'Draft'}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2 },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        };

        html2pdf().set(opt).from(element).save();
    };

    // Helper functions to safely get values for the preview
    const getVal = (key) => formValues?.[key];
    const getDate = () => formValues?.agreementDate ? formValues.agreementDate.format('DD/MM/YYYY') : '__________';
    const getCost = () => formValues?.orderValue ? Number(formValues.orderValue).toLocaleString('en-IN') : '__________';

    return (
        <div style={{ padding: '24px', background: '#f5f7fa', minHeight: '100vh' }}>
            {contextHolder}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <Title level={2} style={{ margin: 0 }}>Vendor Consumer Agreement Generator</Title>
            </div>

            <Card variant="borderless" style={{ marginBottom: 24, borderRadius: 16, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                <Row gutter={[24, 24]} align="middle">
                    <Col xs={24} md={10}>
                        <div style={{ marginBottom: 8, fontWeight: 500 }}>Search Order</div>
                        <Input.Search
                            placeholder="Enter Order ID (e.g. 101)"
                            enterButton={<Button type="primary" icon={<SearchOutlined />}>Fetch Order</Button>}
                            onSearch={handleSearch}
                            loading={loading}
                            size="large"
                            variant="outlined"
                            style={{ width: '100%' }}
                        />
                    </Col>
                    <Col xs={24} md={14}>
                        <div style={{ marginBottom: 8, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}>
                            Recent Orders <Tooltip title="Latest orders in the system"><Tag color="blue" variant="filled">Live</Tag></Tooltip>
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {recentOrders.length > 0 ? (
                                recentOrders.map(order => (
                                    <Tag
                                        key={order.id}
                                        color="blue"
                                        style={{
                                            padding: '4px 12px',
                                            borderRadius: 8,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 4,
                                            margin: 0,
                                            background: '#e6f7ff',
                                            border: '1px solid #91d5ff'
                                        }}
                                        onClick={() => handleSearch(order.id)}
                                    >
                                        <Text strong style={{ color: '#1890ff' }}>#{order.id}</Text>
                                        <Text style={{ fontSize: 12, maxWidth: 100 }} ellipsis title={order.customer?.customerName}>
                                            {order.customer?.customerName}
                                        </Text>
                                    </Tag>
                                ))
                            ) : (
                                <Text type="secondary" style={{ fontSize: 12 }}>No recent orders found</Text>
                            )}
                        </div>
                    </Col>
                </Row>
            </Card>

            <Row gutter={24}>
                {/* Edit Form */}
                <Col span={10}>
                    <Card
                        title={<Space><EditOutlined style={{ color: '#1890ff' }} /> Agreement Details</Space>}
                        variant="borderless"
                        style={{ borderRadius: 16, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', height: '100%' }}
                    >
                        <Form
                            form={form}
                            layout="vertical"
                            initialValues={{
                                termsOfPayment: `● 10% - Advance along with order confirmation / Application on National Portal (www.pmsuryaghar.gov.in)\n● 80% - Signing of this Agreement (In line with Format on National Portal)\n● 5% - Installation of system on the house roof\n● 5% - Installation of Bi-Directional meter and commissioning and approval with MAHADISCOM`
                            }}
                        >
                            <Divider titlePlacement="center" style={{ marginTop: 0 }}>Basic Info</Divider>
                            <Form.Item
                                name="agreementDate"
                                label="Agreement Date"
                                rules={[
                                    { required: true, message: 'Please select agreement date' },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (!value || !orderData?.orderDate || value.isSame(dayjs(orderData.orderDate), 'day') || value.isAfter(dayjs(orderData.orderDate), 'day')) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(new Error(`Agreement date cannot be before Order date (${dayjs(orderData.orderDate).format('DD/MM/YYYY')})`));
                                        },
                                    }),
                                ]}
                            >
                                <DatePicker
                                    format="DD/MM/YYYY"
                                    style={{ width: '100%' }}
                                    disabled={!orderData}
                                    disabledDate={(current) => {
                                        return orderData?.orderDate ? current && current < dayjs(orderData.orderDate).startOf('day') : false;
                                    }}
                                />
                            </Form.Item>

                            <Divider titlePlacement="center">Vendor Details</Divider>
                            <Form.Item name="selectedCompanyId" label="Choose Company Master">
                                <Select
                                    placeholder="Select Company"
                                    onChange={(compId) => {
                                        const comp = companies.find(c => c.id === compId);
                                        if (comp) {
                                            form.setFieldsValue({
                                                vendorName: comp.companyName,
                                                vendorAddress: comp.address
                                            });
                                        }
                                    }}
                                    options={companies.map(c => ({
                                        label: c.companyName,
                                        value: c.id,
                                    }))}
                                />
                            </Form.Item>
                            <Form.Item name="vendorName" label="Vendor Name (from Master)" hidden>
                                <Input readOnly />
                            </Form.Item>
                            <Form.Item name="vendorAddress" label="Vendor Address (from Master)" hidden>
                                <TextArea rows={2} readOnly />
                            </Form.Item>

                            <Divider titlePlacement="center">Customer Details</Divider>
                            <Form.Item name="customerName" label="Customer Name">
                                <Input prefix={<UserOutlined style={{ color: '#bfbfbf' }} />} disabled />
                            </Form.Item>
                            <Form.Item name="consumerNumber" label="Consumer Number">
                                <Input disabled />
                            </Form.Item>
                            <Form.Item name="customerAddress" label="Full Address">
                                <TextArea rows={3} placeholder="House No, Street, District, State, Pin Code" disabled />
                            </Form.Item>

                            <Divider titlePlacement="center">Project Details</Divider>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="projectSize" label="Project Size">
                                        <Input placeholder="e.g. 5 kW" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="orderValue" label="Cost of Plant (INR)">
                                        <Input prefix="₹" type="number" placeholder="Enter amount" />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Divider titlePlacement="center">Payment Schedule</Divider>
                            <Form.Item name="termsOfPayment" label="Terms of Payment">
                                <TextArea rows={8} />
                            </Form.Item>
                        </Form>
                    </Card>
                </Col>

                {/* Preview & Action */}
                <Col span={14}>
                    <Card
                        title={<Space><FileTextOutlined style={{ color: '#52c41a' }} /> Live Preview</Space>}
                        extra={
                            <Button
                                type="primary"
                                icon={<FilePdfOutlined />}
                                onClick={handleGeneratePDF}
                                disabled={!orderData}
                                size="large"
                                style={{ borderRadius: 8 }}
                            >
                                Download Agreement
                            </Button>
                        }
                        variant="borderless"
                        style={{ borderRadius: 16, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', height: '100%' }}
                    >
                        {!orderData ? (
                            <div style={{ textAlign: 'center', padding: '100px 50px', background: '#fafafa', borderRadius: 12, border: '2px dashed #e8e8e8' }}>
                                <FileTextOutlined style={{ fontSize: 48, color: '#bfbfbf', marginBottom: 16 }} />
                                <Typography.Title level={4} style={{ color: '#8c8c8c' }}>Ready to Generate</Typography.Title>
                                <Text type="secondary">Enter an Order ID or select one from the quick access list to preview the agreement.</Text>
                            </div>
                        ) : (
                            <div style={{ background: '#f0f2f5', padding: '20px', borderRadius: 12, overflowY: 'auto', maxHeight: '1000px' }}>
                                <div
                                    id="pdf-content"
                                    ref={componentRef}
                                    style={{
                                        padding: '40px',
                                        fontFamily: 'Times New Roman, serif',
                                        fontSize: '11pt',
                                        color: '#000',
                                        backgroundColor: '#fff',
                                        lineHeight: '1.6',
                                        textAlign: 'justify',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                        minHeight: '297mm', // A4 Height
                                        width: '100%'
                                    }}
                                >
                                    {/* Title Section */}
                                    <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                                        <h3 style={{ margin: 0, fontWeight: 'bold', fontSize: '14pt' }}>
                                            <span style={{ borderBottom: '1.5px solid #000', paddingBottom: '2px' }}>WORK ORDER / CONTRACT / PURCHASE ORDER / AGREEMENT</span>
                                        </h3>
                                        <h4 style={{ margin: '10px 0', fontStyle: 'italic', fontWeight: 'normal', fontSize: '12pt' }}>(Annexure 2 of National Portal)</h4>
                                    </div>

                                    {/* ... same preamble ... */}
                                    <p style={{ marginBottom: '20px' }}>
                                        <strong>Agreement between,</strong><br />
                                        Consumer <strong>{getVal('customerName')}</strong> & Vendor (<strong>{getVal('vendorName')}</strong>) for installation of grid connected rooftop solar (RTS) project (<strong>{getVal('projectSize')}</strong>) under PM – Surya Ghar: Muft Bijli Yojana
                                    </p>

                                    <p style={{ marginBottom: '20px' }}>
                                        This agreement is executed on <strong>{getDate()}</strong> for design, supply, installation, commissioning and 5-year comprehensive maintenance of RTS project/system along with warranty under PM Surya Ghar: Muft Bijli Yojana
                                    </p>

                                    <div style={{ textAlign: 'center', margin: '25px 0', fontWeight: 'bold' }}>Between</div>

                                    <p style={{ marginBottom: '20px' }}>
                                        <strong>{getVal('customerName')}</strong> having Consumer Number - <strong>{getVal('consumerNumber')}</strong> address <strong>{getVal('customerAddress')}</strong> (hereinafter referred to as first Party i.e./consumer/purchaser /owner of system).
                                    </p>

                                    <div style={{ textAlign: 'center', margin: '25px 0', fontWeight: 'bold' }}>And</div>

                                    <p style={{ marginBottom: '20px' }}>
                                        <strong>{getVal('vendorName')}</strong> (Name of Vendor) having registered office at {getVal('vendorAddress')} (hereinafter referred to as second Party i.e. Vendor/ contractor/System Integrator).
                                    </p>

                                    <h4 style={{ marginBottom: '15px', marginTop: '30px', fontSize: '11pt' }}>
                                        <span style={{ borderBottom: '1.5px solid #000', paddingBottom: '1px' }}>Whereas</span>
                                    </h4>

                                    <p style={{ marginBottom: '20px' }}>
                                        The First Party wishes to install a Grid Connected Rooftop Solar Plant (<strong>{getVal('projectSize')}</strong>) on the rooftop of the residential house of the Consumer under PM Surya Ghar: Muft Bijli Yojana.
                                    </p>

                                    <p style={{ marginBottom: '20px' }}>
                                        <strong>And whereas</strong><br />
                                        Second Party has verified availability of appropriate roof and found it feasible to install a Grid Connected Roof Top Solar plant and that the second party is willing to design, supply, install, test, commission and carry out Operation & Maintenance of the Rooftop Solar plant for 5year period.
                                    </p>

                                    <p style={{ marginBottom: '20px' }}>
                                        On this day, the First Party and Second Party agree to the following:
                                    </p>

                                    {/* Clauses Part 1: First Party */}
                                    <div style={{ marginBottom: '30px', marginTop: '30px' }}>
                                        <strong style={{ display: 'block', marginBottom: '15px' }}>
                                            <span style={{ borderBottom: '1.5px solid #000', paddingBottom: '1px' }}>The First Party hereby undertakes to perform the following activities:</span>
                                        </strong>
                                        <ol style={{ paddingLeft: '25px', marginTop: '10px' }}>
                                            <li style={{ marginBottom: '10px' }}>Submission of online application at National Portal for installation of RTS project/system, Submission of application for net-metering and system inspection and upload of the relevant documents on the National Portal of the scheme</li>
                                            <li style={{ marginBottom: '10px' }}>Provide secure storage of the material of the RTS plant delivered at the premises till handover of the system.</li> <br /> <br />
                                            <li style={{ marginBottom: '10px' }}>Provide access to the Roof Top during installation of the plant, operation & maintenance, testing of the plant and equipment and for meter reading from solar meter, inverter etc.</li>
                                            <li style={{ marginBottom: '10px' }}>Provide electricity during plant installation and water for cleaning of the panels.</li>
                                            <li style={{ marginBottom: '10px' }}>Report any malfunctioning of the plant to the Vendor during the warranty period.</li>
                                            <li style={{ marginBottom: '10px' }}>Pay the amount as per the payment schedule as mutually agreed with the vendor, including any additional amount to the second party for any additional work /Customization required depending upon the building condition</li>
                                        </ol>
                                    </div>

                                    {/* Clauses Part 2: Second Party */}
                                    <div style={{ marginBottom: '30px' }}>
                                        <strong style={{ display: 'block', marginBottom: '15px' }}>
                                            <span style={{ borderBottom: '1.5px solid #000', paddingBottom: '1px' }}>The Second Party hereby undertakes to perform the following activities:</span>
                                        </strong>
                                        <ol style={{ paddingLeft: '25px', marginTop: '10px' }}>
                                            <li style={{ marginBottom: '10px' }}>The Vendor must follow all the standards and safety guidelines prescribed under state regulations and technical standards prescribed by MNRE for RTS projects, failing which the vendor is liable for blacklisting from participation in the govt. project/ scheme and other penal actions in accordance with the law. The responsibility of supply, installation and commissioning of the rooftop solar project/system in complete compliance with MNRE scheme guidelines lies with the Vendor.</li>
                                            <li style={{ marginBottom: '10px' }}><strong>Site Survey:</strong> Site visit, survey and development of detailed project report for installation of RTS system. This also includes, feasibility study of roof, strength of roof and shadow free area. If any additional work or customization is involved for the plant installation as per site condition and requirement of the consumer building, the Vendor shall prepare an estimate and can raise a separate invoice including GST in addition to the amount towards standard plant cost. The consumer shall pay the amount for such additional work directly to the Vendor.</li>
                                            <li style={{ marginBottom: '10px' }}><strong>Design & Engineering:</strong> Design of plant along with drawings and selection of components as per standard provided by the DISCOM/SERC/MNRE for best performance and safety of the plant.</li>
                                            <li style={{ marginBottom: '10px' }}><strong>Module and Inverter:</strong> The solar modules, including the solar cells, should be manufactured in India. Both the solar modules and inverters shall conform to the relevant standards and specifications prescribed by MNRE. Any other requirement, viz. star labeling (solar modules), quality control orders and standards & labeling (inverters) etc., shall also be complied.</li>
                                            <li style={{ marginBottom: '10px' }}><strong>Procurement & Supply:</strong> Procurement of complete system as per BIS/IS/IEC standard (whatever applicable) & safety guidelines for installation of rooftop solar plants. The supplied materials should comply with all MNRE standards for release of subsidy.</li>
                                            <li style={{ marginBottom: '10px' }}><strong>Installation & Civil work:</strong> Complete civil work, structure work and electrical work (including drawings) following all the safety and relevant BIS standards.</li>
                                            <li style={{ marginBottom: '10px' }}><strong>Documentation (Technical Catalogues/Warranty Certificates/BIS certificates/other test reports etc):</strong> All such documents shall be provided to the consumer for online uploading and submission of technical specifications, IEC/BIS report, Sr. Nos, Warranty card of Solar Panel & Inverter, Layout & Electrical SLD, Structure Design and Drawing, Cable and other detailed documents.</li>
                                            <li style={{ marginBottom: '10px' }}><strong>Project completion report (PCR):</strong> Assisting the consumer in filling and uploading of signed documents (Consumer & Vendor) on the national portal.</li>  <br /> <br />
                                            <li style={{ marginBottom: '10px' }}><strong>Warranty:</strong> System warranty certificates should be provided to the consumer. The complete system should be warranted for 5 years from the date of commissioning by DISCOM. Individual component warranty documents provided by the manufacturer shall be provided to the consumer and all possible assistance should be extended to the consumer for claiming the warranty from the manufacturer.</li>
                                            <li style={{ marginBottom: '10px' }}><strong>NET meter & Grid Connectivity:</strong> Net meter supply/procurement, testing and approvals shall be in the scope of vendor. Grid connection of the plant shall be in the scope of the vendor.</li>
                                            <li style={{ marginBottom: '10px' }}><strong>Testing and Commissioning:</strong> The vendor shall be present at the time of testing and commissioning by the DISCOM.</li>
                                            <li style={{ marginBottom: '10px' }}><strong>Operation & Maintenance:</strong> Five (5) years Comprehensive Operation and Maintenance including overhauling, wear and tear and regular checking of healthiness of system at proper interval shall be in the scope of vendor. The vendor shall also educate the consumer on best practices for cleaning of the modules and system maintenance.</li>
                                            <li style={{ marginBottom: '10px' }}><strong>Insurance:</strong> Any insurance cost pertaining to material transfer/storage before commissioning of the system shall be in the scope of the vendor.</li>
                                            <li style={{ marginBottom: '10px' }}><strong>Applicable Standard:</strong> The system must meet the technical standards and specifications notified by MNRE. The vendor is solely responsible to supply components and service which meets the technical standards and specification prescribed by MNRE and State DISCOMs.</li>

                                            {/* Clause 15: Payment Terms */}
                                            <li style={{ marginBottom: '20px' }}>
                                                <strong>Project/system cost & payment terms:</strong> The cost of the plant and payment schedule shall be as per mutually discussed and decided between the vendor and consumer. The agreed terms are as under:
                                                <div style={{ margin: '15px 0', border: '1px solid #ddd', padding: '15px', backgroundColor: '#fafafa' }}>
                                                    <p style={{ marginBottom: '15px' }}><strong>Cost of Plant:</strong> INR {getCost()}/- Including GST.+Load extension charges (At actuals to be paid to MSEB)</p>
                                                    <p style={{ fontWeight: 'bold', marginBottom: '10px' }}>
                                                        <span style={{ borderBottom: '1.5px solid #000', paddingBottom: '1px' }}>Terms of Payment:</span>
                                                    </p>
                                                    <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.8' }}>{getVal('termsOfPayment')}</div>
                                                </div>
                                            </li>

                                            <li style={{ marginBottom: '10px' }}><strong>Dispute:</strong> In-case of any dispute between consumer and vendor (in supply/installation/maintenance of system or payment terms), both parties must settle the same mutually or as per law. MNRE/DISCOM shall not be liable for, and would not be a party to any dispute arising between vendor and consumer.</li>
                                            <li style={{ marginBottom: '10px' }}><strong>Subsidy / Project Related Documents:</strong> Vendor must provide all the documents to the consumer and help in uploading the same to National Portal for smooth release of subsidy.</li>
                                            <li style={{ marginBottom: '30px' }}><strong>Performance of Plant:</strong> The Performance Ratio (PR) of Plant must be 75% at the time of commissioning of the project by DISCOM or its authorized agency. Vendor must provide (returnable basis) radiation sensor with valid calibration certificate of any NABL / International laboratory at the time of commissioning / testing of the plant. Vendor must maintain the PR of the plant till warranty of project i.e. 5 years from the date of commissioning.</li>
                                            <li style={{ marginBottom: '10px' }}><strong>Mutually Agreed Terms of Payment ...</strong><br />As included in clause 15</li>
                                        </ol>
                                    </div>

                                    {/* Signatures */}
                                    <div style={{ marginTop: '54px', display: 'flex', justifyContent: 'space-between', pageBreakInside: 'avoid' }}>
                                        <div style={{ width: '45%' }}>
                                            <p style={{ fontWeight: 'bold', marginBottom: '10px' }}>First Party</p>
                                            <p>Name: <strong>{getVal('customerName')}</strong></p>
                                            <p>Address: {getVal('customerAddress')}</p>
                                            {<div style={{ marginTop: '34px', marginBottom: '50px', borderTop: '0.5px solid #cececeff' }}></div>}
                                            <div style={{ height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '5px' }}>
                                                <div style={{ fontWeight: 'bold' }}>Sign:</div>
                                                <div style={{ width: '100px', height: '100%' }}></div>
                                            </div>
                                            <p>Date: {getDate()}</p>
                                        </div>
                                        <div style={{ width: '45%' }}>
                                            <p style={{ fontWeight: 'bold', marginBottom: '10px' }}>Second Party</p>
                                            <p>Name: <strong>{getVal('vendorName')}</strong></p>
                                            <p>Address: {getVal('vendorAddress')}</p>
                                            {<div style={{ marginTop: '54px', marginBottom: '50px', borderTop: '0.25px solid #cececeff' }}></div>}
                                            <div style={{ height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '35px' }}>
                                                <div style={{ fontWeight: 'bold' }}>Sign:</div>
                                                <div style={{ height: '50px' }}>
                                                    <img src={vendorSign} alt="Vendor Signature" style={{ maxHeight: '100%', maxWidth: '150px' }} />
                                                </div>
                                            </div>
                                            <p>Date: {getDate()}</p>
                                        </div>
                                    </div>

                                    <div style={{ marginTop: '54px', fontSize: '10pt', fontStyle: 'italic', borderTop: '1.5px solid #eee', paddingTop: '15px' }}>
                                        Disclaimer: This agreement is between vendor and consumer and any dispute related to the same shall not involve any third party including MNRE and Distribution Utilities.
                                    </div>
                                </div>
                            </div>
                        )}
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default VendorAgreementPage;
